import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Clock, Rocket } from 'lucide-react';

const Certifications: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const placeholders = [
    { icon: '🏆', title: 'Professional Certs', desc: 'Industry-recognized developer certifications', color: '#3b82f6', glow: 'rgba(59,130,246,0.15)' },
    { icon: '⭐', title: 'Achievement Awards', desc: 'Outstanding performance recognition', color: '#8b5cf6', glow: 'rgba(139,92,246,0.15)' },
    { icon: '🎖️', title: 'Course Completions', desc: 'Specialized training programs', color: '#10b981', glow: 'rgba(16,185,129,0.15)' },
    { icon: '🚀', title: 'Project Milestones', desc: 'Major deliveries & achievements', color: '#f59e0b', glow: 'rgba(245,158,11,0.15)' },
  ];

  return (
    <section id="certifications" className="py-20 lg:py-32 relative overflow-hidden bg-[#ffffff]">
      {/* Background - Soft Liquid Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <motion.div
          animate={{
            transform: ['translate(0%, 0%) scale(1)', 'translate(5%, 10%) scale(1.1)', 'translate(0%, 0%) scale(1)'],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
        <motion.div
          animate={{
            transform: ['translate(0%, 0%) scale(1)', 'translate(-5%, -10%) scale(1.05)', 'translate(0%, 0%) scale(1)'],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full"
            style={{
              background: 'rgba(255,255,255,0.7)',
              border: '1px solid #ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <span className="text-sm font-medium tracking-wide" style={{ color: '#475569', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Milestones
            </span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: '#0f172a' }}
          >
            Certifications &{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Achievements
            </span>
          </h2>

          <p className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: '#475569', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            Professional certifications and milestones in my development journey. Continuous learning and growth.
          </p>
        </motion.div>

        {/* Coming Soon hero card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl overflow-hidden mb-10"
          style={{
            background: 'rgba(255,255,255,0.7)',
            border: '1px solid #ffffff',
            boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
          }}
        >
          {/* Animated inner liquid glow */}
          <motion.div
            animate={{
              background: [
                'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(139,92,246,0.06) 0%, transparent 50%)',
                'radial-gradient(ellipse at 80% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(ellipse at 20% 70%, rgba(139,92,246,0.06) 0%, transparent 50%)',
                'radial-gradient(ellipse at 50% 20%, rgba(139,92,246,0.08) 0%, transparent 50%), radial-gradient(ellipse at 50% 80%, rgba(59,130,246,0.08) 0%, transparent 50%)',
                'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(139,92,246,0.06) 0%, transparent 50%)',
              ],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0 pointer-events-none"
          />

          <div className="relative z-10 text-center py-20 px-8 flex flex-col items-center">
            {/* Icon */}
            <motion.div
              animate={{
                y: [-4, 4, -4],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="w-20 h-20 mx-auto mb-8 rounded-3xl flex items-center justify-center shadow-lg"
              style={{
                background: 'rgba(255,255,255,0.7)',
                border: '1px solid #ffffff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <Award style={{ color: '#3b82f6' }} size={36} strokeWidth={1.5} />
            </motion.div>

            <h3
              className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: '#0f172a' }}
            >
              Coming Soon
            </h3>

            <p
              className="text-base max-w-lg mx-auto mb-8 leading-relaxed"
              style={{ color: '#475569', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
            >
              This section will showcase my professional certifications and achievements. Stay tuned for updates as I continue my learning journey.
            </p>

            {/* Status badge - Soft Glass Pill */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <div
                className="flex items-center gap-2 px-5 py-2 rounded-full"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  border: '1px solid #ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Clock size={14} style={{ color: '#8b5cf6' }} />
                <span className="text-xs font-medium tracking-wide" style={{ color: '#475569', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  In Development
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full max-w-xs mx-auto mb-10">
              <div
                className="w-full h-1.5 rounded-full overflow-hidden mb-3 shadow-inner"
                style={{ background: 'rgba(0,0,0,0.05)' }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={isInView ? { width: '75%' } : {}}
                  transition={{ duration: 1.5, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)' }}
                />
              </div>
              <span className="text-xs font-medium tracking-wide" style={{ color: '#64748b', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                75% Complete
              </span>
            </div>

            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-semibold transition-all duration-300 shadow-lg"
              style={{
                background: 'rgba(255,255,255,0.7)',
                border: '1px solid #ffffff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                color: '#0f172a',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                backdropFilter: 'blur(10px)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.9)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.7)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
              }}
            >
              <Rocket size={16} className="text-[#3b82f6]" />
              Stay Updated
            </motion.button>
          </div>
        </motion.div>

        {/* Placeholder cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {placeholders.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="rounded-3xl p-6 text-center cursor-default transition-all duration-500"
              style={{
                background: 'rgba(255,255,255,0.7)',
                border: '1px solid #ffffff',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.9)';
                (e.currentTarget as HTMLElement).style.boxShadow = `0 15px 35px -5px ${item.glow}`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.7)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 30px rgba(0,0,0,0.05)';
              }}
            >
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: i * 0.4, ease: 'easeInOut' }}
                className="text-3xl mb-4 inline-block drop-shadow-md"
              >
                {item.icon}
              </motion.div>
              <h4
                className="font-bold text-base mb-2 tracking-tight"
                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: '#0f172a' }}
              >
                {item.title}
              </h4>
              <p className="text-sm leading-relaxed mb-5" style={{ color: '#475569', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                {item.desc}
              </p>
              <span
                className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  border: '1px solid #ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  color: item.color,
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                }}
              >
                Coming Soon
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certifications;